/**
 * The slice of `Storage` the voice preferences use, so the tests can hand in a
 * Map and the app can hand in localStorage.
 *
 * `browserStorage()` wraps every call in try/catch, like every other
 * `fiveout:` key in the app: private mode, a full quota or a storage-blocking
 * embed must cost a preference, never the voice feature or the table under it.
 */
/**
 * Every voice preference and the mute list live under this prefix, which is how
 * account deletion finds them all, including any added after it was written.
 */
export const VOICE_KEY_PREFIX = "fiveout:voice.";

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function browserStorage(): KeyValueStorage {
  return {
    getItem(key) {
      try {
        return globalThis.localStorage?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        globalThis.localStorage?.setItem(key, value);
      } catch {
        /* the setting lasts for this page instead */
      }
    },
    removeItem(key) {
      try {
        globalThis.localStorage?.removeItem(key);
      } catch {
        /* nothing to undo */
      }
    },
  };
}

/** JSON out of storage, or null when it is missing or unreadable. */
export function readJson(storage: KeyValueStorage, key: string): unknown {
  const raw = storage.getItem(key);
  if (raw === null || raw.trim() === "") return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}
